import React, { useState } from "react";
import { CircleCheck, CloudUpload, X, Loader2, AlertCircle } from "lucide-react";

const API_BASE = import.meta.env.VITE_BACKEND_URL;

export default function Return() {
  const [form, setForm] = useState({
    orderId: "",
    name: "",
    email: "",
    phone: "",
    reason: "",
    message: "",
    image: "",
  });

  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  /* -------------------- VALIDATION -------------------- */
  const validateForm = () => {
    const errors = {};
    
    if (!form.orderId.trim()) {
      errors.orderId = "Order ID is required";
    }
    
    if (!form.name.trim()) {
      errors.name = "Name is required";
    }
    
    if (!form.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = "Invalid email format";
    }
    
    if (!form.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!/^[0-9]{10}$/.test(form.phone.replace(/[\s-]/g, ''))) {
      errors.phone = "Phone number must be 10 digits";
    }
    
    if (!form.reason) {
      errors.reason = "Please select a return reason";
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* -------------------- IMAGE UPLOAD (CLOUDINARY) -------------------- */
  const uploadImage = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      setError("Please upload a valid image");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB");
      return;
    }

    setUploading(true);
    setError("");

    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", "veshh_return_form");

    try {
      const res = await fetch(
        "https://api.cloudinary.com/v1_1/dmrlotfsv/image/upload",
        {
          method: "POST",
          body: data,
        }
      );

      if (!res.ok) {
        throw new Error("Upload failed");
      }

      const json = await res.json();
      setForm((p) => ({ ...p, image: json.secure_url }));
    } catch (err) {
      setError("Image upload failed. Please try again.");
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  /* -------------------- SUBMIT RETURN -------------------- */
  const submitReturn = async () => {
    if (!validateForm()) {
      setError("Please fix the errors above");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/returns`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderId: form.orderId.trim(),
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          reason: form.reason,
          message: form.message.trim(),
          images: form.image ? [{ url: form.image }] : [],
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Return request failed");
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Submit error:", err);
      setError(err.message || "Failed to submit return request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* -------------------- SUCCESS STATE -------------------- */
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md text-center">
          <CircleCheck className="mx-auto mb-4 text-green-600" size={48} />
          <h2 className="text-2xl font-bold mb-2">Return Request Received</h2>
          <p className="text-gray-600">
            We have received your return request. Our team will carefully review it and get back to you shortly.
          </p>
        </div>
      </div>
    );
  }

  /* -------------------- FORM -------------------- */
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto bg-white p-8 rounded-xl shadow-lg">
        <h1 className="text-3xl font-bold mb-6 text-center">
          VESHH-EON Return Request
        </h1>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="mb-4">
          <input
            className={`w-full border p-3 rounded ${fieldErrors.orderId ? 'border-red-500' : ''}`}
            placeholder="Order ID *"
            value={form.orderId}
            onChange={(e) => {
              setForm({ ...form, orderId: e.target.value });
              setFieldErrors({ ...fieldErrors, orderId: '' });
            }}
          />
          {fieldErrors.orderId && (
            <p className="text-red-500 text-xs mt-1">{fieldErrors.orderId}</p>
          )}
        </div>

        <div className="mb-4">
          <input
            className={`w-full border p-3 rounded ${fieldErrors.name ? 'border-red-500' : ''}`}
            placeholder="Full Name *"
            value={form.name}
            onChange={(e) => {
              setForm({ ...form, name: e.target.value });
              setFieldErrors({ ...fieldErrors, name: '' });
            }}
          />
          {fieldErrors.name && (
            <p className="text-red-500 text-xs mt-1">{fieldErrors.name}</p>
          )}
        </div>

        <div className="mb-4">
          <input
            type="email"
            className={`w-full border p-3 rounded ${fieldErrors.email ? 'border-red-500' : ''}`}
            placeholder="Email *"
            value={form.email}
            onChange={(e) => {
              setForm({ ...form, email: e.target.value });
              setFieldErrors({ ...fieldErrors, email: '' });
            }}
          />
          {fieldErrors.email && (
            <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
          )}
        </div>

        <div className="mb-4">
          <input
            className={`w-full border p-3 rounded ${fieldErrors.phone ? 'border-red-500' : ''}`}
            placeholder="Phone Number (10 digits) *"
            value={form.phone}
            onChange={(e) => {
              setForm({ ...form, phone: e.target.value });
              setFieldErrors({ ...fieldErrors, phone: '' });
            }}
          />
          {fieldErrors.phone && (
            <p className="text-red-500 text-xs mt-1">{fieldErrors.phone}</p>
          )}
        </div>

        <div className="mb-4">
          <select
            className={`w-full border p-3 rounded ${fieldErrors.reason ? 'border-red-500' : ''}`}
            value={form.reason}
            onChange={(e) => {
              setForm({ ...form, reason: e.target.value });
              setFieldErrors({ ...fieldErrors, reason: '' });
            }}
          >
            <option value="">Select return reason *</option>
            <option value="Size issue">Size issue</option>
            <option value="Damaged product">Damaged product</option>
            <option value="Wrong item received">Wrong item received</option>
            <option value="Other">Other</option>
          </select>
          {fieldErrors.reason && (
            <p className="text-red-500 text-xs mt-1">{fieldErrors.reason}</p>
          )}
        </div>

        <textarea
          className="w-full border p-3 rounded mb-4"
          placeholder="Additional details (optional)"
          rows={4}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
        />

        {!form.image ? (
          <label className="block border-dashed border-2 p-6 rounded mb-4 text-center cursor-pointer hover:bg-gray-50 transition">
            {uploading ? (
              <Loader2 className="animate-spin mx-auto text-gray-600" />
            ) : (
              <>
                <CloudUpload className="mx-auto mb-2 text-gray-600" />
                <span className="text-sm text-gray-600">
                  Upload image (optional, max 5MB)
                </span>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              hidden
              disabled={uploading}
              onChange={(e) => uploadImage(e.target.files[0])}
            />
          </label>
        ) : (
          <div className="flex items-center justify-between border p-3 rounded mb-4 bg-green-50">
            <span className="text-sm text-green-600">Image uploaded</span>
            <button 
              onClick={() => setForm({ ...form, image: "" })}
              className="text-gray-600 hover:text-gray-800"
            >
              <X size={18} />
            </button>
          </div>
        )}

        <button
          disabled={submitting || uploading}
          onClick={submitReturn}
          className="w-full bg-black text-white py-3 rounded hover:bg-gray-800 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="animate-spin" size={18} />
              Submitting...
            </span>
          ) : (
            "Submit Return Request"
          )}
        </button>
      </div>
    </div>
  );
}
